import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      if (mutation.attributeName === 'data-product-id') {
        this.dataset.recommendationsPerformed = 'false';
        this.#abortController?.abort();
      }

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * In-flight load promise so tab switch + intersection observer don't race
   * @type {Promise<void> | null}
   */
  #loadPromise = null;

  /**
   * In-flight fetch promises keyed by URL
   * @type {Record<string, Promise<{ success: true, data: string } | { success: false, status: number }>>}
   */
  #fetchPromises = {};

  /**
   * Abort controller for the active recommendations fetch
   * @type {AbortController | null}
   */
  #abortController = null;

  /**
   * Product id for the in-flight load (stale-response guard)
   * @type {string | null}
   */
  #loadingProductId = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#abortController?.abort();
  }

  /**
   * Loads recommendations on demand (e.g. when the recommendations tab is selected).
   */
  loadRecommendations() {
    this.#loadRecommendations();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    if (this.#loadPromise) return this.#loadPromise;

    this.#loadPromise = this.#performLoad().finally(() => {
      this.#loadPromise = null;
    });

    return this.#loadPromise;
  }

  async #performLoad() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.classList.remove('hidden');
    delete this.dataset.error;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#loadingProductId = productId;

    try {
      const result = await this.#fetchCachedRecommendations(productId, sectionId, intent, signal);

      if (signal.aborted || this.dataset.productId !== this.#loadingProductId) return;

      if (!result.success) {
        // The Theme Editor will place a section element element in the DOM whose section_id is not available
        // to the Section Renderer API. In this case, we can safely ignore the error.
        if (!Shopify.designMode) {
          this.#handleError(new Error(`Server returned ${result.status}`));
        }
        return;
      }

      const html = document.createElement('div');
      html.innerHTML = result.data || '';
      const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);
      const hasRecommendations =
        recommendations?.querySelector('[data-has-recommendations="true"]') != null ||
        (recommendations?.querySelector('[data-has-recommendations]') == null &&
          Boolean(recommendations?.querySelector('.resource-list__item')));

      if (recommendations?.innerHTML?.trim() && hasRecommendations) {
        this.dataset.recommendationsPerformed = 'true';
        this.classList.remove('hidden');
        this.innerHTML = recommendations.innerHTML;
        // The tabs section pages this list with its own "Load more"; it has no other way
        // to know the items just arrived.
        this.dispatchEvent(new CustomEvent('product-recommendations:updated', { bubbles: true }));
      } else {
        this.#handleError(new Error('No recommendations available'));
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (signal.aborted || this.dataset.productId !== this.#loadingProductId) return;
      this.#handleError(error instanceof Error ? error : new Error('Error loading product recommendations'));
    }
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @param {AbortSignal} [signal]
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent, signal) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    if (this.#fetchPromises[url]) {
      return this.#fetchPromises[url];
    }

    this.#fetchPromises[url] = (async () => {
      try {
        const response = await fetch(url, signal ? { signal } : undefined);
        if (!response.ok) {
          return { success: false, status: response.status };
        }

        const text = await response.text();
        this.#cachedRecommendations[url] = text;
        return { success: true, data: text };
      } finally {
        delete this.#fetchPromises[url];
      }
    })();

    return this.#fetchPromises[url];
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
